// src/utils/formatoTextoUtils.js
// Utilidades para renderizado de texto enriquecido (Negrita, Cursiva, Subrayado) en Facturación y Presupuestos

/**
 * Escapa caracteres HTML para seguridad
 */
function escapeHtmlBasico(str) {
    if (str === undefined || str === null) return '';
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

/**
 * Sanea y repara marcas de formato redundantes, anidadas en exceso o corruptas
 * (ej. ****, *****, <u><u>, etc.)
 */
export function sanearMarcasFormato(texto) {
    if (!texto) return '';
    let limpio = String(texto);

    // 1. Corregir asteriscos redundantes consecutivos (3 o más)
    limpio = limpio.replace(/\*{3,}/g, '**');

    // 2. Corregir etiquetas <u> duplicadas consecutivas: <u><u> -> <u>, </u></u> -> </u>
    limpio = limpio.replace(/(<u>\s*)+/gi, '<u>');
    limpio = limpio.replace(/(<\/u>\s*)+/gi, '</u>');

    // 3. Corregir etiquetas vacías redundantes: ****, <u></u>
    limpio = limpio.replace(/\*\*(\s*)\*\*/g, '$1');
    limpio = limpio.replace(/<u>(\s*)<\/u>/gi, '$1');

    return limpio;
}

/**
 * Convierte sintaxis enriquecida (Markdown y tags básicos) en HTML estilizado seguro:
 * - **negrita** o <b>negrita</b> -> <strong style="font-weight: 700; color: inherit;">negrita</strong>
 * - *cursiva* o <i>cursiva</i> -> <em style="font-style: italic;">cursiva</em>
 * - <u>subrayado</u> o __subrayado__ -> <u style="text-decoration: underline;">subrayado</u>
 * Preserva caracteres especiales, saltos de línea y viñetas sin inyecciones arbitrarias.
 */
export function formatearTextoEnriquecidoHtml(texto) {
    if (!texto) return '';

    // 0. Sanear marcas residuales o corruptas
    const textoSaneado = sanearMarcasFormato(texto);

    // 1. Escapar HTML base para garantizar seguridad
    let seguro = escapeHtmlBasico(textoSaneado);

    // 2. Parsear negritas: **texto** o &lt;b&gt;texto&lt;/b&gt;
    seguro = seguro.replace(/\*\*(.*?)\*\*/g, '<strong style="font-weight: 700; color: inherit;">$1</strong>');
    seguro = seguro.replace(/&lt;b&gt;(.*?)&lt;\/b&gt;/gi, '<strong style="font-weight: 700; color: inherit;">$1</strong>');

    // 3. Parsear cursiva: *texto* o &lt;i&gt;texto&lt;/i&gt; (sin colisionar con **)
    seguro = seguro.replace(/(?<!\*)\*(?!\*)(.*?)(?<!\*)\*(?!\*)/g, '<em style="font-style: italic;">$1</em>');
    seguro = seguro.replace(/&lt;i&gt;(.*?)&lt;\/i&gt;/gi, '<em style="font-style: italic;">$1</em>');

    // 4. Parsear subrayado: &lt;u&gt;texto&lt;/u&gt; o __texto__
    seguro = seguro.replace(/&lt;u&gt;(.*?)&lt;\/u&gt;/gi, '<u style="text-decoration: underline;">$1</u>');
    seguro = seguro.replace(/__(.*?)__/g, '<u style="text-decoration: underline;">$1</u>');

    return seguro;
}

/**
 * Comprueba si una cadena contiene marcas de texto enriquecido
 */
export function tieneFormatoEnriquecido(str) {
    if (!str) return false;
    return /\*\*.*?\*\*|\*.*?\*|<u>.*?<\/u>|&lt;u&gt;.*?&lt;\/u&gt;|__.*?__/s.test(str);
}

/**
 * Detecta qué formatos de tipografía están actualmente activos en la selección o bajo el cursor.
 * Devuelve { bold: boolean, italic: boolean, underline: boolean }
 */
export function detectarFormatosActivos(textoCompleto, start, end) {
    if (!textoCompleto) {
        return { bold: false, italic: false, underline: false };
    }

    const s = Math.min(start ?? 0, end ?? 0);
    const e = Math.max(start ?? 0, end ?? 0);

    const textoSel = s < e ? textoCompleto.substring(s, e) : '';
    const antes = textoCompleto.substring(0, s);
    const despues = textoCompleto.substring(e);

    // Detección de Negrita:
    const boldEnSel = /\*\*.*?\*\*|<b>.*?<\/b>|<strong>.*?<\/strong>/.test(textoSel) || 
                      (textoSel.startsWith('**') && textoSel.endsWith('**') && textoSel.length >= 4);
    const conteoBoldAntes = (antes.match(/\*\*/g) || []).length;
    const conteoBoldDespues = (despues.match(/\*\*/g) || []).length;
    const boldCircundante = (conteoBoldAntes % 2 === 1) && (conteoBoldDespues > 0);

    const isBold = boldEnSel || boldCircundante;

    // Detección de Subrayado:
    const underlineEnSel = /<u>.*?<\/u>|__.*?__/.test(textoSel) ||
                           (textoSel.startsWith('<u>') && textoSel.endsWith('</u>') && textoSel.length >= 7) ||
                           (textoSel.startsWith('__') && textoSel.endsWith('__') && textoSel.length >= 4);
    const conteoUOpenAntes = (antes.match(/<u>/gi) || []).length;
    const conteoUCloseAntes = (antes.match(/<\/u>/gi) || []).length;
    const underlineCircundante = (conteoUOpenAntes > conteoUCloseAntes) && /<\/u>/i.test(despues);

    const isUnderline = underlineEnSel || underlineCircundante;

    // Detección de Cursiva:
    const sinBoldSel = textoSel.replace(/\*\*/g, '');
    const italicEnSel = /(?<!\*)\*(?!\*).*?(?<!\*)\*(?!\*)/.test(sinBoldSel) ||
                        /<i>.*?<\/i>|<em>.*?<\/em>/.test(textoSel);
    const antesSinBold = antes.replace(/\*\*/g, '');
    const despuesSinBold = despues.replace(/\*\*/g, '');
    const conteoItalicAntes = (antesSinBold.match(/\*/g) || []).length;
    const conteoItalicDespues = (despuesSinBold.match(/\*/g) || []).length;
    const italicCircundante = (conteoItalicAntes % 2 === 1) && (conteoItalicDespues > 0);

    const isItalic = italicEnSel || italicCircundante;

    return {
        bold: isBold,
        italic: isItalic,
        underline: isUnderline
    };
}

/**
 * Alterna (aplica o remueve) un formato sobre el texto y rango seleccionados.
 * Bloquea la duplicación: si ya tiene el formato, lo retira en lugar de encimar tags repetidos.
 */
export function alternarFormatoEnTexto(textoOriginal, start, end, tipo) {
    if (textoOriginal === undefined || textoOriginal === null) textoOriginal = '';

    let s = Math.min(start ?? textoOriginal.length, end ?? textoOriginal.length);
    let e = Math.max(start ?? textoOriginal.length, end ?? textoOriginal.length);

    // 1. Si no hay selección (cursor en un punto)
    if (s === e) {
        let palabraInicio = s;
        let palabraFin = e;
        while (palabraInicio > 0 && !/\s/.test(textoOriginal[palabraInicio - 1])) {
            palabraInicio--;
        }
        while (palabraFin < textoOriginal.length && !/\s/.test(textoOriginal[palabraFin])) {
            palabraFin++;
        }

        if (palabraFin > palabraInicio) {
            s = palabraInicio;
            e = palabraFin;
        } else {
            const tagAbrir = tipo === 'bold' ? '**' : tipo === 'italic' ? '*' : '<u>';
            const tagCerrar = tipo === 'bold' ? '**' : tipo === 'italic' ? '*' : '</u>';
            const muestra = tipo === 'bold' ? 'texto negrita' : tipo === 'italic' ? 'texto cursiva' : 'texto subrayado';
            const nuevo = textoOriginal.substring(0, s) + tagAbrir + muestra + tagCerrar + textoOriginal.substring(e);
            return {
                nuevoTexto: sanearMarcasFormato(nuevo),
                nuevoStart: s + tagAbrir.length,
                nuevoEnd: s + tagAbrir.length + muestra.length,
                activo: true
            };
        }
    }

    // 2. Analizar si el formato ya está activo en la selección
    const formatos = detectarFormatosActivos(textoOriginal, s, e);
    const estaActivo = formatos[tipo];

    const textoSel = textoOriginal.substring(s, e);
    const antes = textoOriginal.substring(0, s);
    const despues = textoOriginal.substring(e);

    let nuevoTexto = '';
    let nuevoStart = s;
    let nuevoEnd = e;

    if (estaActivo) {
        // ==========================================
        // YA ESTÁ ACTIVO -> QUITAR FORMATO (BLOQUEO DE DUPLICADOS)
        // ==========================================
        if (tipo === 'bold') {
            let limpioSel = textoSel.replace(/\*\*/g, '');
            let nuevoAntes = antes;
            let nuevoDespues = despues;
            if (nuevoAntes.endsWith('**') && nuevoDespues.startsWith('**')) {
                nuevoAntes = nuevoAntes.slice(0, -2);
                nuevoDespues = nuevoDespues.slice(2);
            }
            nuevoTexto = nuevoAntes + limpioSel + nuevoDespues;
            nuevoStart = nuevoAntes.length;
            nuevoEnd = nuevoStart + limpioSel.length;
        } else if (tipo === 'underline') {
            let limpioSel = textoSel.replace(/<u>/gi, '').replace(/<\/u>/gi, '').replace(/__/g, '');
            let nuevoAntes = antes;
            let nuevoDespues = despues;
            if (nuevoAntes.toLowerCase().endsWith('<u>') && nuevoDespues.toLowerCase().startsWith('</u>')) {
                nuevoAntes = nuevoAntes.slice(0, -3);
                nuevoDespues = nuevoDespues.slice(4);
            }
            nuevoTexto = nuevoAntes + limpioSel + nuevoDespues;
            nuevoStart = nuevoAntes.length;
            nuevoEnd = nuevoStart + limpioSel.length;
        } else if (tipo === 'italic') {
            let limpioSel = textoSel.replace(/(?<!\*)\*(?!\*)/g, '');
            let nuevoAntes = antes;
            let nuevoDespues = despues;
            if (nuevoAntes.endsWith('*') && !nuevoAntes.endsWith('**') && nuevoDespues.startsWith('*') && !nuevoDespues.startsWith('**')) {
                nuevoAntes = nuevoAntes.slice(0, -1);
                nuevoDespues = nuevoDespues.slice(1);
            }
            nuevoTexto = nuevoAntes + limpioSel + nuevoDespues;
            nuevoStart = nuevoAntes.length;
            nuevoEnd = nuevoStart + limpioSel.length;
        }
    } else {
        // ==========================================
        // NO ESTÁ ACTIVO -> APLICAR FORMATO LIMPIAMENTE (1 SOLA VEZ)
        // ==========================================
        const matchEspaciosInicio = (textoSel.match(/^(\s*)/) || [''])[0];
        const matchEspaciosFin = (textoSel.match(/(\s*)$/) || [''])[0];
        const contenidoCentral = textoSel.substring(matchEspaciosInicio.length, textoSel.length - matchEspaciosFin.length);

        if (!contenidoCentral) {
            return {
                nuevoTexto: textoOriginal,
                nuevoStart: s,
                nuevoEnd: e,
                activo: false
            };
        }

        let tagAbrir = '';
        let tagCerrar = '';
        if (tipo === 'bold') {
            tagAbrir = '**';
            tagCerrar = '**';
        } else if (tipo === 'underline') {
            tagAbrir = '<u>';
            tagCerrar = '</u>';
        } else if (tipo === 'italic') {
            tagAbrir = '*';
            tagCerrar = '*';
        }

        const envuelto = `${matchEspaciosInicio}${tagAbrir}${contenidoCentral}${tagCerrar}${matchEspaciosFin}`;
        nuevoTexto = antes + envuelto + despues;
        nuevoStart = s + matchEspaciosInicio.length;
        nuevoEnd = nuevoStart + tagAbrir.length + contenidoCentral.length + tagCerrar.length;
    }

    // Sanear marcas residuales para evitar acumulaciones corruptas
    nuevoTexto = sanearMarcasFormato(nuevoTexto);

    return {
        nuevoTexto,
        nuevoStart,
        nuevoEnd,
        activo: !estaActivo
    };
}
